import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Mail, Plus, Search, MoreVertical, Edit, Trash2, Send, Pause, Play,
  RefreshCw, Eye, BarChart3, Clock, CheckCircle, AlertCircle, XCircle,
  Calendar, Users, TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'secondary', icon: Edit },
  scheduled: { label: 'Scheduled', color: 'default', icon: Clock },
  sending: { label: 'Sending', color: 'default', icon: RefreshCw },
  sent: { label: 'Sent', color: 'default', icon: CheckCircle },
  paused: { label: 'Paused', color: 'secondary', icon: Pause },
  cancelled: { label: 'Cancelled', color: 'secondary', icon: XCircle },
  failed: { label: 'Failed', color: 'destructive', icon: AlertCircle },
};

export default function MarketingCampaigns() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  const token = sessionStorage.getItem('auth_token');
  const limit = 20;

  useEffect(() => {
    if (token) {
      fetchCampaigns();
    }
  }, [token, page, search, statusFilter]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        limit: limit.toString()
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/api/marketing/campaigns?${params}`);
      if (response.data.success) {
        setCampaigns(response.data.campaigns);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const deleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"? This action cannot be undone.`)) return;
    
    try {
      const response = await axios.delete(
        `${API}/api/marketing/campaigns/${campaign.id}?token=${token}`
      );
      if (response.data.success) {
        toast.success('Campaign deleted');
        fetchCampaigns();
      } else {
        toast.error(response.data.error || 'Failed to delete campaign');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  const pauseCampaign = async (campaign) => {
    try {
      const response = await axios.post(
        `${API}/api/marketing/campaigns/${campaign.id}/pause?token=${token}`
      );
      if (response.data.success) {
        toast.success('Campaign paused');
        fetchCampaigns();
      } else {
        toast.error(response.data.error || 'Failed to pause campaign');
      }
    } catch (error) {
      toast.error('Failed to pause campaign');
    }
  };

  const resumeCampaign = async (campaign) => {
    try {
      const response = await axios.post(
        `${API}/api/marketing/campaigns/${campaign.id}/resume?token=${token}`
      );
      if (response.data.success) {
        toast.success('Campaign resumed');
        fetchCampaigns();
      } else {
        toast.error(response.data.error || 'Failed to resume campaign');
      }
    } catch (error) {
      toast.error('Failed to resume campaign');
    }
  };

  const sendCampaignNow = async (campaign) => {
    if (!window.confirm(`Send campaign "${campaign.name}" immediately?`)) return;
    
    try {
      const response = await axios.post(
        `${API}/api/marketing/campaigns/${campaign.id}/send?token=${token}`
      );
      if (response.data.success) {
        toast.success(`Campaign sent! ${response.data.sent} emails delivered.`);
        fetchCampaigns();
      } else {
        toast.error(response.data.error || 'Failed to send campaign');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = config.icon;
    return (
      <Badge variant={config.color} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredCampaigns = activeTab === 'all' 
    ? campaigns 
    : campaigns.filter(c => c.status === activeTab);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="marketing-campaigns">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="text-gray-500 mt-1">
            Create, schedule, and track your email marketing campaigns
          </p>
        </div>
        <Button onClick={() => navigate('/marketing/campaigns/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign
        </Button>
      </div>

      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="draft">Drafts</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search & Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search campaigns..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={fetchCampaigns}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Mail className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
            <p className="text-gray-500 mb-6">Create your first email campaign to start engaging your audience</p>
            <Button onClick={() => navigate('/marketing/campaigns/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredCampaigns.map((campaign) => (
            <Card key={campaign.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Mail className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 
                          className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600"
                          onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
                        >
                          {campaign.name}
                        </h3>
                        {getStatusBadge(campaign.status)}
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-1 mb-3">
                        Subject: {campaign.subject}
                      </p>
                      
                      {/* Stats Row */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-1 text-gray-500">
                          <Users className="h-4 w-4" />
                          <span>{campaign.stats?.total_recipients || 0} recipients</span>
                        </div>
                        
                        {campaign.status === 'sent' && (
                          <>
                            <div className="flex items-center gap-1 text-green-600">
                              <TrendingUp className="h-4 w-4" />
                              <span>
                                {campaign.stats?.sent > 0 
                                  ? ((campaign.stats?.opened / campaign.stats?.sent) * 100).toFixed(1)
                                  : '0'}% opened
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-blue-600">
                              <span>
                                {campaign.stats?.sent > 0 
                                  ? ((campaign.stats?.clicked / campaign.stats?.sent) * 100).toFixed(1)
                                  : '0'}% clicked
                              </span>
                            </div>
                          </>
                        )}
                        
                        {campaign.scheduled_at && campaign.status === 'scheduled' && (
                          <div className="flex items-center gap-1 text-amber-600">
                            <Calendar className="h-4 w-4" />
                            <span>Scheduled: {formatDate(campaign.scheduled_at)}</span>
                          </div>
                        )}
                        
                        {campaign.sent_at && (
                          <div className="flex items-center gap-1 text-gray-400">
                            <Clock className="h-4 w-4" />
                            <span>Sent: {formatDate(campaign.sent_at)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {campaign.status === 'draft' && (
                      <Button 
                        size="sm"
                        onClick={() => navigate(`/marketing/campaigns/${campaign.id}/edit`)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                    
                    {campaign.status === 'sent' && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
                      >
                        <BarChart3 className="h-4 w-4 mr-1" />
                        Analytics
                      </Button>
                    )}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        
                        {campaign.status === 'draft' && (
                          <>
                            <DropdownMenuItem onClick={() => navigate(`/marketing/campaigns/${campaign.id}/edit`)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Campaign
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => sendCampaignNow(campaign)}>
                              <Send className="h-4 w-4 mr-2" />
                              Send Now
                            </DropdownMenuItem>
                          </>
                        )}
                        
                        {campaign.status === 'sending' && (
                          <DropdownMenuItem onClick={() => pauseCampaign(campaign)}>
                            <Pause className="h-4 w-4 mr-2" />
                            Pause Sending
                          </DropdownMenuItem>
                        )}
                        
                        {campaign.status === 'paused' && (
                          <DropdownMenuItem onClick={() => resumeCampaign(campaign)}>
                            <Play className="h-4 w-4 mr-2" />
                            Resume Sending
                          </DropdownMenuItem>
                        )}
                        
                        {campaign.status === 'sent' && (
                          <DropdownMenuItem onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}>
                            <BarChart3 className="h-4 w-4 mr-2" />
                            View Analytics
                          </DropdownMenuItem>
                        )}
                        
                        <DropdownMenuSeparator />
                        
                        {['draft', 'cancelled'].includes(campaign.status) && (
                          <DropdownMenuItem 
                            onClick={() => deleteCampaign(campaign)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} campaigns
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
